import React from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { useRefreshCandidateSession } from 'src/services/api/mutations/candidateMutations';
import { useToast } from 'src/hooks/use-toast';
import { cn } from 'src/lib/utils';
import { getToneButtonClasses } from 'src/lib/statusStyles';
import { registerAction } from './actionRegistry';

// 'slate' — a plain neutral utility action — see actionColors.js.
const tone = getToneButtonClasses('slate');

// "Refresh" — re-checks a single candidate's test session status against
// the test provider. Ported from the old RequirementPipeline.jsx's
// handleRefreshCandidateSession (Pending tab only, TA-only). Unlike
// AssignTestAction this operates on one candidate at a time (it's rendered
// per-row, not from a bulk bar), so it takes a single candidateId rather
// than an array.
const RefreshCandidateSessionAction = ({
  candidateId,
  opportunityId,
  onSuccess,
}) => {
  const { toast } = useToast();
  const refreshMutation = useRefreshCandidateSession();

  const handleClick = async (e) => {
    e.stopPropagation();
    try {
      const response = await refreshMutation.mutateAsync({
        candidateId,
        opportunityId,
      });

      if (response.success) {
        toast({
          message:
            response.message || 'Candidate status refreshed successfully',
          variant: 'success',
        });
        onSuccess(response);
      } else {
        toast({
          message: response.message || 'Failed to refresh status',
          variant: 'warning',
        });
      }
    } catch (error) {
      toast({
        message: error.message || 'Failed to refresh status',
        variant: 'destructive',
      });
    }
  };

  const isRefreshing = refreshMutation.isPending;

  return (
    <Button
      variant="tone"
      size="sm"
      className={cn(
        'flex items-center gap-1 text-xs px-2.5',
        tone.bg,
        tone.text,
        tone.border,
        tone.hoverBg
      )}
      disabled={isRefreshing}
      onClick={handleClick}
    >
      {isRefreshing ? (
        <>
          <Loader2 className={cn('h-4 w-4 animate-spin', tone.text)} />
          Refreshing...
        </>
      ) : (
        <span>Refresh</span>
      )}
    </Button>
  );
};

RefreshCandidateSessionAction.propTypes = {
  candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
};

RefreshCandidateSessionAction.defaultProps = {
  opportunityId: undefined,
  onSuccess: () => {},
};

registerAction({
  stateMachineKey: 'default',
  actionKey: 'refresh_candidate_session',
  component: RefreshCandidateSessionAction,
});

export default RefreshCandidateSessionAction;
